import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { PiRejectedNotice } from '../components/PiRejectedNotice';
import { piErrorMessage } from '../lib/piActions';
import { ArrowLeft, Trophy, XCircle, Clock, CalendarCheck, FileText, WarningCircle, CheckCircle, Upload, DownloadSimple, Package, Timer, FilePdf, ArrowsLeftRight, WarningOctagon, Lock, ArrowClockwise, Briefcase } from '@phosphor-icons/react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTenantPath } from '../lib/tenantPath';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import useAuthStore from '../store/authStore';
import PaymentsPanel from '../components/PaymentsPanel';
import PrevNextNav from '../components/PrevNextNav';
import PaymentTermsPicker, { emptyPaymentTerms, isStandardTerms, totalPercent, canonicalTermsKey } from '../components/PaymentTermsPicker';
import SCViewOnlyBanner from '../components/SCViewOnlyBanner';
import { ScGatedButton } from '../components/ScGatedButton';
import { LeadNotesPanel } from '../components/LeadNotesPanel';
import FreightCostPanel from '../components/FreightCostPanel';

import { confirm } from '../components/ConfirmDialog';
import { notify } from '../lib/notify';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LeadDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const tp = useTenantPath();
  const { user } = useAuthStore();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showWonModal, setShowWonModal] = useState(false);  const [showExtendModal, setShowExtendModal] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);

  // Order Won state
  const [wonQuotationId, setWonQuotationId] = useState('');
  // Feb 2026 — live PI status for the currently-selected winning quote.
  const [wonPiStatus, setWonPiStatus] = useState(null); // {has_pi, status?, pi_number?, id?}
  const [wonPiGenerating, setWonPiGenerating] = useState(false);
  const [poNumber, setPoNumber] = useState('');
  const [poFile, setPoFile] = useState(null);
  const [wonSubmitting, setWonSubmitting] = useState(false);
  const [paymentDate, setPaymentDate] = useState('');
  const [paymentUtr, setPaymentUtr] = useState('');
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentScreenshot, setPaymentScreenshot] = useState(null);
  const [paymentNotes, setPaymentNotes] = useState('');
  const [actualFreight, setActualFreight] = useState('');
  const canEditFreight = ['Vice President', 'President', 'Sales Coordinator'].includes(user?.role);
  // iter132 — Structured payment terms (replaces free-text notes). Default is
  // the standard 50/50 split; any non-standard combo goes to VP approval.
  const [paymentTerms, setPaymentTerms] = useState(() => emptyPaymentTerms());
  // iter132.2 — templates saved for this customer (VP-approved combos that
  // can bypass approval next time). Fetched when the Won modal opens.
  const [customerTemplates, setCustomerTemplates] = useState([]);

  // Extend state
  const [extensionDays, setExtensionDays] = useState(15);

  // Order Complete state
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [wccFile, setWccFile] = useState(null);
  const [cscFile, setCscFile] = useState(null);
  const [completeSubmitting, setCompleteSubmitting] = useState(false);

  // Reassign state
  const canReassign = ['National Sales Manager', 'Vice President', 'President'].includes(user?.role);
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [salesExecs, setSalesExecs] = useState([]);
  const [reassignTarget, setReassignTarget] = useState('');
  const [reassignReason, setReassignReason] = useState('');
  const [reassignSubmitting, setReassignSubmitting] = useState(false);
  const [crossRegionInfo, setCrossRegionInfo] = useState(null); // {lead_region, target_region, target_name}
  const [reassignHistory, setReassignHistory] = useState([]);

  // Feb 2026 — President-only reopen for accidentally-closed leads.
  const [reopenBusy, setReopenBusy] = useState(false);
  const [lastReopen, setLastReopen] = useState(null);
  const handleReopenLead = async () => {
    const reason = window.prompt(
      'Reopen this lead? This will:\n' +
      '• Flip status back to In Progress and re-enable quoting\n' +
      '• Restore the original SE forecast value (if we have it)\n' +
      '• Clear any Won record + payment schedule + logged payments\n\n' +
      'Enter a short reason (audit trail):'
    );
    if (reason === null) return; // user cancelled
    setReopenBusy(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/leads/${id}/reopen`, { reason: reason || '' }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchLead();
    } catch (e) {
      notify(e?.response?.data?.detail || 'Failed to reopen lead');
    } finally {
      setReopenBusy(false);
    }
  };

  const openReassignModal = async () => {
    setReassignTarget('');
    setReassignReason('');
    setCrossRegionInfo(null);
    setShowReassignModal(true);
    try {
      const [usersResp, histResp] = await Promise.all([
        axios.get(`${API}/users/sales-executives`),
        axios.get(`${API}/leads/${id}/reassignments`),
      ]);
      setSalesExecs(usersResp.data.users || []);
      setReassignHistory(histResp.data.items || []);
    } catch (err) {
      notify('Failed to load executives');
    }
  };

  const submitReassign = async (force = false) => {
    if (!reassignTarget) { notify('Please select an executive'); return; }
    setReassignSubmitting(true);
    try {
      const resp = await axios.post(`${API}/leads/${id}/reassign`, {
        new_assignee_id: reassignTarget,
        force_cross_region: force,
        reason: reassignReason,
      });
      setShowReassignModal(false);
      setCrossRegionInfo(null);
      fetchLead();
      const cross = resp.data?.cross_region ? ' (cross-region — RSMs notified)' : '';
      notify(`Lead reassigned successfully${cross}`);
    } catch (err) {
      const status = err.response?.status;
      const data = err.response?.data;
      if (status === 409 && data?.detail === 'region_mismatch') {
        setCrossRegionInfo({
          lead_region: data.lead_region,
          target_region: data.target_region,
          target_name: data.target_name,
        });
      } else {
        notify(data?.detail || 'Failed to reassign lead');
      }
    } finally { setReassignSubmitting(false); }
  };

  useEffect(() => { fetchLead(); }, [id]);

  // Feb 2026 (iter85) — book-style navigation: pull the same lead list the
  // index page shows so ← / Prev / Next can jump between records without
  // bouncing back to /leads. Fetched once on mount; the visible ordering
  // matches the default /api/leads sort (created_at DESC).
  const [leadList, setLeadList] = useState([]);
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/leads`).then((r) => {
      if (!cancelled) setLeadList(Array.isArray(r.data) ? r.data : []);
    }).catch(() => { /* nav just won't render — non-fatal */ });
    return () => { cancelled = true; };
  }, []);
  const navItems = leadList.map((l) => ({ id: l.id, label: `${l.name}${l.company ? ' — ' + l.company : ''}` }));

  const fetchLead = async () => {
    try {
      const resp = await axios.get(`${API}/leads/${id}/detail`);
      setLead(resp.data);
      // Fire-and-forget reopen chip data. Cheap, safe to ignore errors.
      try {
        const rr = await axios.get(`${API}/leads/${id}/last-reopen`);
        setLastReopen(rr.data);
      } catch { setLastReopen(null); }
    } catch (error) { console.error(error); }
    finally { setLoading(false); }
  };

  // Feb 2026 — refresh PI status inline in the Order Won modal whenever the
  // SE picks a winning quote. Empty selection or a fetch failure clears the
  // badge so we never render stale info.
  useEffect(() => {
    if (!showWonModal || !wonQuotationId) { setWonPiStatus(null); return; }
    let cancelled = false;
    axios.get(`${API}/proforma-invoices/by-quotation/${wonQuotationId}`)
      .then(r => { if (!cancelled) setWonPiStatus(r.data); })
      .catch(() => { if (!cancelled) setWonPiStatus(null); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wonQuotationId, showWonModal]);

  // iter132.2 — When the Won modal opens, fetch any VP-approved terms
  // templates saved for this lead's customer so the SE can preview whether
  // their combination will bypass approval.
  useEffect(() => {
    if (!showWonModal || !lead?.customer_id) { setCustomerTemplates([]); return; }
    let cancelled = false;
    axios.get(`${API}/customer-terms-templates`, { params: { customer_id: lead.customer_id } })
      .then(r => { if (!cancelled) setCustomerTemplates(r.data?.items || []); })
      .catch(() => { if (!cancelled) setCustomerTemplates([]); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showWonModal, lead?.customer_id]);

  const generatePiInline = async () => {
    if (!wonQuotationId || wonPiGenerating) return;
    setWonPiGenerating(true);
    try {
      const r = await axios.post(`${API}/quotations/${wonQuotationId}/generate-pi`);
      const pi = r.data?.proforma_invoice || r.data;
      setWonPiStatus({ has_pi: true, id: pi.id, pi_number: pi.pi_number, status: pi.status });
      notify(`Proforma Invoice ${pi.pi_number} created and sent for approval (${pi.status === 'approved' ? 'auto-approved' : 'pending'}). Mark Won once it is approved.`);
    } catch (err) {
      notify(piErrorMessage(err, 'Could not generate PI.'));
    } finally {
      setWonPiGenerating(false);
    }
  };


  const handleOrderWon = async () => {
    if (!wonQuotationId || !poNumber) { notify('Please select a quotation and enter PO number'); return; }
    if (!paymentDate || !paymentUtr || !paymentScreenshot) {
      notify('Payment Date, UTR/Transaction Ref, and Payment Screenshot are required.');
      return;
    }
    // iter132 — validate structured terms client-side so we don't waste an
    // upload round trip. The server re-validates for safety.
    const pctTotal = totalPercent(paymentTerms);
    const anyEnabled = ['advance_with_po', 'before_dispatch', 'on_installation', 'credit'].some(k => paymentTerms[k]?.enabled);
    if (!anyEnabled) { notify('Select at least one payment term.'); return; }
    if (Math.abs(pctTotal - 100) > 0.01) { notify(`Payment terms must total 100% (currently ${pctTotal.toFixed(1)}%).`); return; }
    if (paymentTerms.credit?.enabled && !(Number(paymentTerms.credit?.days) > 0)) {
      notify('Credit term requires a positive number of days.'); return;
    }
    setWonSubmitting(true);
    try {
      let poFileUrl = '';
      if (poFile) {
        const formData = new FormData();
        formData.append('file', poFile);
        const uploadResp = await axios.post(`${API}/leads/${id}/po-upload`, formData);
        poFileUrl = uploadResp.data.file_url;
      }
      let paymentUrl = '';
      if (paymentScreenshot) {
        const fd = new FormData();
        fd.append('file', paymentScreenshot);
        const up = await axios.post(`${API}/leads/${id}/po-upload`, fd);
        paymentUrl = up.data.file_url;
      }
      const resp = await axios.post(`${API}/leads/${id}/order-won`, {
        quotation_id: wonQuotationId,
        po_number: poNumber,
        po_file_url: poFileUrl,
        payment_date: paymentDate,
        payment_utr: paymentUtr,
        payment_amount: Number(paymentAmount || 0),
        payment_screenshot_url: paymentUrl,
        payment_notes: paymentNotes,
        payment_terms: paymentTerms,
        ...(canEditFreight && actualFreight !== '' ? { actual_freight_cost: Number(actualFreight) } : {}),
      });
      setShowWonModal(false);
      if (resp.data?.status === 'pending_approval') {
        notify(resp.data.message || 'Sent to VP for approval. Lead will be marked Won once approved.');
      } else if (resp.data?.template_bypass) {
        notify(
          `Order marked as Won.\n\n` +
          `Approval was auto-bypassed because these terms (${resp.data.template_bypass.summary}) match a saved template for ${resp.data.template_bypass.customer_name || 'this customer'}.`,
        );
      }
      fetchLead();
    } catch (error) {
      // Feb 2026 — PI-before-Won guard. Backend returns a structured detail
      // when the winning quote lacks an approved PI so we can offer the
      // right CTA instead of a raw error.
      const detail = error.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.pi_status) {
        if (detail.pi_status === 'missing') {
          if (await confirm({ message: `${detail.message}\n\nGenerate the PI now?` })) {
            try {
              const r = await axios.post(`${API}/quotations/${detail.quotation_id}/generate-pi`);
              const pi = r.data?.proforma_invoice || r.data;
              const url = '/proforma-invoices';
              navigate(url);
              notify('Proforma Invoice created — visible at the top of the PI list. Submit it for approval, then come back here to mark Won.');
            } catch (pe) {
              notify(pe?.response?.data?.detail || 'Could not generate PI. Contact your manager.');
            }
          }
        } else {
          if (await confirm({ message: `${detail.message}\n\nOpen the PI list to check its status?` })) {
            navigate('/proforma-invoices');
          }
        }
      } else {
        notify(typeof detail === 'string' ? detail : (detail?.message || 'Error'));
      }
    }
    finally { setWonSubmitting(false); }
  };

  const handleExtend = async () => {
    try {
      await axios.post(`${API}/leads/${id}/extend`, { days: extensionDays });
      setShowExtendModal(false);
      fetchLead();
    } catch (error) { notify(error.response?.data?.detail || 'Error'); }
  };

  const handleOrderComplete = async () => {
    if (!invoiceFile && !wccFile && !cscFile) { notify('Please upload at least one document'); return; }
    setCompleteSubmitting(true);
    try {
      const formData = new FormData();
      if (invoiceFile) formData.append('invoice_file', invoiceFile);
      if (wccFile) formData.append('wcc_file', wccFile);
      if (cscFile) formData.append('csc_file', cscFile);
      await axios.post(`${API}/leads/${id}/order-complete`, formData);
      setShowCompleteModal(false);
      fetchLead();
    } catch (error) {
      // Feb 2026 (iter81) — backend now returns a dict detail for the
      // payment-shortfall guard: { message, booked, received, shortfall, code }.
      const d = error.response?.data?.detail;
      const msg = (d && typeof d === 'object') ? (d.message || JSON.stringify(d)) : (d || 'Error completing order');
      notify(msg);
    }
    finally { setCompleteSubmitting(false); }
  };

  const downloadDoc = (type) => {
    const token = localStorage.getItem('token') || '';
    window.open(`${API}/leads/${id}/${type}-pdf?token=${token}`, '_blank');
  };

  if (loading) return <div className="flex items-center justify-center h-full"><div className="animate-spin w-8 h-8 border-4 border-[#00a69c] border-t-transparent rounded-full" /></div>;
  if (!lead) return <div className="flex items-center justify-center h-full text-[#6D8E87]">Lead not found</div>;

  const isTerminal = ['won', 'lost', 'completed'].includes(lead.status);
  const isWon = lead.status === 'won';
  const isCompleted = lead.status === 'completed';

  // Feb 2026 (iter81) — Payment-parity guard on the Order Complete flow.
  // A won lead can only be marked "completed" once received payments match
  // the booked value. We compute here so the button + modal can show the
  // shortfall inline (and stay disabled until it's fully collected).
  const bookedValue = Number(
    lead.won_data?.final_billing_value
    ?? lead.final_billing_value
    ?? lead.lead_value
    ?? 0
  );
  const paidValue = Number(lead.paid_amount || 0);
  const paymentShortfall = Math.max(0, Math.round((bookedValue - paidValue) * 100) / 100);
  const isFullyPaid = bookedValue <= 0 || paymentShortfall <= 0.01;

  // Delivery countdown
  const deliveryDue = lead.won_data?.delivery_due_date ? new Date(lead.won_data.delivery_due_date) : null;
  const now = new Date();
  const daysRemaining = deliveryDue ? Math.ceil((deliveryDue - now) / (1000 * 60 * 60 * 24)) : null;
  const isOverdue = daysRemaining !== null && daysRemaining < 0;
  const isUrgent = daysRemaining !== null && daysRemaining >= 0 && daysRemaining <= 7;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto" data-testid="lead-detail-page">
      <SCViewOnlyBanner record={lead} className="mb-4" />
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button onClick={() => navigate(tp('/leads'))} className="w-10 h-10 flex items-center justify-center text-[#00a69c] hover:bg-[#E2F3F0] rounded-lg transition" data-testid="back-button">
          <ArrowLeft size={24} />
        </button>
        <PrevNextNav
          items={navItems}
          currentId={id}
          onNavigate={(newId) => navigate(tp(`/leads/${newId}`))}
          testIdPrefix="lead"
        />
        <div className="flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="app-page-title" style={{ fontFamily: 'Outfit' }}>{lead.name}</h1>
            <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${
              lead.status === 'won' ? 'bg-emerald-100 text-emerald-700' :
              lead.status === 'completed' ? 'bg-blue-100 text-blue-700' :
              lead.status === 'lost' ? 'bg-red-100 text-red-700' :
              lead.status === 'extended' ? 'bg-amber-100 text-amber-700' :
              'bg-gray-100 text-gray-700'
            }`} data-testid="lead-status-badge">{lead.status}</span>
            {canReassign && !isTerminal && (
              <button onClick={openReassignModal}
                className="flex items-center gap-1.5 px-3 py-1 text-xs font-semibold border border-[#00a69c] text-[#00a69c] rounded-full hover:bg-[#E2F3F0] transition"
                data-testid="reassign-lead-btn"
                title="Reassign this lead to another executive">
                <ArrowsLeftRight size={14} weight="bold" /> Reassign
              </button>
            )}
            {lead.origin_tender_id && (
              <button
                onClick={() => navigate(tp(`/tenders/${lead.origin_tender_id}`))}
                className="flex items-center gap-1.5 px-3 py-1 text-xs font-semibold bg-[#EEF2FF] text-[#4338CA] border border-[#C7D2FE] rounded-full hover:bg-[#E0E7FF] transition"
                data-testid="lead-open-source-tender"
                title="Open the tender this lead was created from"
              >
                <Briefcase size={14} weight="fill" /> From Tender → Open
              </button>
            )}
          </div>
          <p className="text-[#6D8E87]">
            {lead.company} {lead.customer_code && `(${lead.customer_code})`}
            {lead.created_by_name && <span className="ml-2 text-xs">· Owned by <span className="font-medium text-[#3B5A54]">{lead.created_by_name}</span>{lead.region && ` (${lead.region})`}</span>}
            {lead.uploaded_by_name && lead.uploaded_by !== lead.created_by && (
              <span className="ml-2 text-xs">· Uploaded by <span className="font-medium text-[#3B5A54]">{lead.uploaded_by_name}</span></span>
            )}
          </p>
        </div>
      </div>

      {/* Feb 2026 — Reopen audit chip: shows the last reopen event for the team's context. */}
      {lastReopen?.has_reopen && (
        <div
          className="mb-4 flex items-center gap-2 px-3 py-2 rounded-full bg-amber-50 border border-amber-200 text-xs text-amber-900 w-fit"
          data-testid="reopen-audit-chip"
          title={lastReopen.reason || 'No reason recorded'}
        >
          <ArrowClockwise size={14} weight="bold" className="text-amber-700" />
          <span>
            Reopened from <span className="font-semibold">{(lastReopen.from_status || '').toUpperCase()}</span>{' '}
            by <span className="font-semibold">{lastReopen.reopened_by_name}</span> on{' '}
            {new Date(lastReopen.reopened_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
            {lastReopen.reason ? <span className="italic"> · “{lastReopen.reason}”</span> : ''}
          </span>
        </div>
      )}

      {/* Lead Info Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="medical-card p-4">
          <p className="text-xs text-[#6D8E87]">Value</p>
          <p className="text-lg font-bold text-[#00a69c]" data-testid="lead-value-display">₹{(lead.lead_value || 0).toLocaleString('en-IN')}</p>
          {lead.initial_lead_value != null && Math.abs((lead.initial_lead_value || 0) - (lead.lead_value || 0)) > 0.01 && (
            <p className="text-[10px] text-[#6D8E87] mt-0.5" data-testid="lead-initial-forecast">
              Forecast at intake: ₹{(lead.initial_lead_value || 0).toLocaleString('en-IN')}
            </p>
          )}
        </div>
        <div className="medical-card p-4"><p className="text-xs text-[#6D8E87]">Location</p><p className="text-sm font-medium">{lead.city}, {lead.state}</p></div>
        <div className="medical-card p-4"><p className="text-xs text-[#6D8E87]">Email</p><p className="text-sm font-medium truncate">{lead.email}</p></div>
        <div className="medical-card p-4"><p className="text-xs text-[#6D8E87]">Phone</p><p className="text-sm font-medium">{lead.phone}</p></div>
      </div>

      {/* Delivery Countdown (for won leads) */}
      {(isWon || isCompleted) && deliveryDue && (
        <div className={`medical-card p-5 mb-6 border-2 ${isOverdue ? 'border-red-400 bg-red-50' : isUrgent ? 'border-amber-400 bg-amber-50' : 'border-blue-300 bg-blue-50'}`} data-testid="delivery-countdown">
          <div className="flex items-center gap-4">
            <div className={`w-14 h-14 rounded-full flex items-center justify-center ${isOverdue ? 'bg-red-100' : isUrgent ? 'bg-amber-100' : 'bg-blue-100'}`}>
              <Timer size={28} className={isOverdue ? 'text-red-600' : isUrgent ? 'text-amber-600' : 'text-blue-600'} weight="bold" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-[#052A23]">Delivery {isCompleted ? 'Was Due' : 'Timeline'}</p>
              <p className="text-xs text-[#6D8E87]">Due: {deliveryDue.toLocaleDateString()} ({lead.won_data.delivery_days} days from order)</p>
            </div>
            <div className="text-right">
              {isOverdue ? (
                <div><p className="text-2xl font-bold text-red-600" style={{ fontFamily: 'Outfit' }}>{Math.abs(daysRemaining)} days</p><p className="text-xs text-red-500 font-semibold">OVERDUE</p></div>
              ) : daysRemaining === 0 ? (
                <div><p className="text-2xl font-bold text-amber-600" style={{ fontFamily: 'Outfit' }}>TODAY</p><p className="text-xs text-amber-500 font-semibold">DUE TODAY</p></div>
              ) : (
                <div><p className="text-2xl font-bold text-blue-600" style={{ fontFamily: 'Outfit' }}>{daysRemaining} days</p><p className="text-xs text-blue-500">remaining</p></div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Quotations */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="lg:col-span-2 medical-card p-5" data-testid="quotations-section">
          <h3 className="text-lg font-semibold text-[#052A23] mb-3" style={{ fontFamily: 'Outfit' }}>Quotations ({lead.quotation_count})</h3>
          {lead.quotations?.length > 0 ? (
            <div className="space-y-2">
              {lead.quotations.map(q => (
                <div key={q.id} className="flex items-center justify-between p-3 bg-[#F4F9F7] rounded-lg hover:bg-[#E2F3F0] cursor-pointer transition" onClick={() => navigate(`/quotations/${q.id}`)}>
                  <div><p className="text-sm font-medium text-[#052A23]">₹{q.total?.toLocaleString('en-IN')}</p><p className="text-xs text-[#6D8E87]">{q.items?.length} items • {new Date(q.created_at).toLocaleDateString()}</p></div>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${q.status === 'approved' ? 'bg-green-100 text-green-700' : q.status === 'rejected' ? 'bg-red-100 text-red-700' : q.status === 'pending_approval' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-700'}`}>{q.status}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-[#6D8E87]">No quotations yet</p>
          )}
          {!isTerminal && user?.role !== 'Tender Executive' && (
            <button onClick={() => navigate(`/quotations/create?lead_id=${id}`)} className="w-full mt-3 px-4 py-2 border border-[#00a69c] text-[#00a69c] rounded-lg text-sm font-medium hover:bg-[#E2F3F0] transition" data-testid="create-quote-btn">
              + Create Quotation
            </button>
          )}
          {isTerminal && (
            <div
              className="mt-3 p-3 rounded-lg border border-dashed border-[#B8D5CE] bg-[#F4F9F7] text-xs text-[#3B5A54] flex items-start gap-2"
              data-testid="quoting-closed-banner"
            >
              <Lock size={14} className="text-[#6D8E87] mt-0.5 shrink-0" weight="bold" />
              <div>
                <p className="font-semibold text-[#052A23]">Quoting is closed for this lead.</p>
                <p className="mt-0.5 text-[#6D8E87]">
                  Lead is <span className="font-medium">{lead.status.toUpperCase()}</span> — new quotes and edits to existing drafts are blocked.
                  {user?.role === 'President'
                    ? ' Use “Reopen Lead” below if this was a mistake.'
                    : ' Ask the President to reopen the lead if you need to add or edit quotes.'}
                </p>
              </div>
            </div>
          )}
          {isTerminal && user?.role === 'President' && (
            <button
              onClick={handleReopenLead}
              disabled={reopenBusy}
              className="w-full mt-3 px-4 py-2 border border-amber-500 text-amber-700 rounded-lg text-sm font-semibold hover:bg-amber-50 transition disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid="reopen-lead-btn"
            >
              {reopenBusy ? 'Reopening…' : '↺ Reopen Lead'}
            </button>
          )}
        </div>

        {/* Reminders */}
        <div className="medical-card p-5" data-testid="reminders-section">
          <h3 className="text-lg font-semibold text-[#052A23] mb-3" style={{ fontFamily: 'Outfit' }}>Reminders</h3>
          {lead.reminders?.length > 0 ? (
            <div className="space-y-2">
              {lead.reminders.map((r) => {
                const isPast = new Date(r.due_date) < new Date();
                return (
                  <div key={r.id} className={`flex items-start gap-2 p-2.5 rounded-lg text-sm ${isPast ? 'bg-red-50 border border-red-200' : 'bg-[#F4F9F7] border border-[#D5E8E4]'}`}>
                    {isPast ? <WarningCircle size={16} className="text-red-500 mt-0.5 shrink-0" /> : <CalendarCheck size={16} className="text-[#00a69c] mt-0.5 shrink-0" />}
                    <div><p className="text-[#052A23]">{r.message}</p><p className={`text-xs ${isPast ? 'text-red-500' : 'text-[#6D8E87]'}`}>{new Date(r.due_date).toLocaleDateString()}</p></div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-[#6D8E87]">No reminders set</p>
          )}
        </div>

        <LeadNotesPanel leadId={id} />
      </div>

      {/* Won Data */}
      {lead.won_data && (
        <div className="medical-card p-5 mb-6 border-2 border-emerald-300" data-testid="won-data-section">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold text-emerald-700" style={{ fontFamily: 'Outfit' }}>Order Won Details</h3>            {/* Docs Download */}
            <div className="flex gap-2" data-testid="docs-download">
              <button onClick={() => downloadDoc('wcc')} className="flex items-center gap-1.5 px-3 py-1.5 bg-[#00a69c] text-white rounded-lg text-xs font-semibold hover:bg-[#00bfb3] transition" data-testid="download-wcc-btn">
                <FilePdf size={14} weight="bold" /> WCC
              </button>
              <button onClick={() => downloadDoc('csc')} className="flex items-center gap-1.5 px-3 py-1.5 bg-[#00a69c] text-white rounded-lg text-xs font-semibold hover:bg-[#00bfb3] transition" data-testid="download-csc-btn">
                <FilePdf size={14} weight="bold" /> CSC
              </button>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <div><p className="text-xs text-[#6D8E87]">Final Billing</p><p className="text-lg font-bold text-emerald-700">₹{lead.won_data.final_billing_value?.toLocaleString('en-IN')}</p></div>
            <div><p className="text-xs text-[#6D8E87]">PO Number</p><p className="text-sm font-medium">{lead.won_data.po_number}</p></div>
            <div><p className="text-xs text-[#6D8E87]">Won By</p><p className="text-sm font-medium">{lead.won_data.won_by_name}</p></div>
            <div><p className="text-xs text-[#6D8E87]">Date</p><p className="text-sm font-medium">{new Date(lead.won_data.created_at).toLocaleDateString()}</p></div>
          </div>
          {(lead.won_data.transportation || lead.won_data.payment_terms) && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 p-3 bg-emerald-50 rounded-lg mb-3" data-testid="won-tc-section">
              {lead.won_data.transportation && <div><p className="text-xs text-emerald-600">Transportation</p><p className="text-sm font-medium">{lead.won_data.transportation}</p></div>}
              {lead.won_data.payment_terms && <div><p className="text-xs text-emerald-600">Payment Terms</p><p className="text-sm font-medium">{lead.won_data.payment_terms}</p></div>}
              {lead.won_data.warranty_years > 0 && <div><p className="text-xs text-emerald-600">Warranty</p><p className="text-sm font-medium">{lead.won_data.warranty_years} Year{lead.won_data.warranty_years > 1 ? 's' : ''}</p></div>}
              {lead.won_data.installation && <div><p className="text-xs text-emerald-600">Installation</p><p className="text-sm font-medium">{lead.won_data.installation}</p></div>}
              {lead.won_data.delivery_days > 0 && <div><p className="text-xs text-emerald-600">Delivery</p><p className="text-sm font-medium">{lead.won_data.delivery_days} Days</p></div>}
            </div>
          )}
          {lead.won_data.po_file_url && (
            <a href={`${BACKEND_URL}${lead.won_data.po_file_url}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-[#00a69c] hover:underline"><FileText size={16} /> View PO Copy</a>
          )}
          {canEditFreight && (() => {
            const wq = (lead.quotations || []).find((q) => q.id === lead.won_data.quotation_id);
            return wq ? <FreightCostPanel quotation={wq} onSaved={fetchLead} /> : null;
          })()}
        </div>
      )}

      {/* Slice 2 (Feb 2026) — Payments panel. Only meaningful for won/completed leads. */}
      {(lead.status === 'won' || lead.status === 'completed') && (
        <PaymentsPanel
          leadId={lead.id}
          currentUserRole={user?.role}
          onStatusChange={(newStatus) => {
            if (newStatus && newStatus !== lead.status) {
              setLead(prev => prev ? { ...prev, status: newStatus } : prev);
            }
          }}
        />
      )}

      {/* Completion Data */}
      {lead.completion_data && (
        <div className="medical-card p-5 mb-6 border-2 border-blue-300" data-testid="completion-section">
          <h3 className="text-lg font-semibold text-blue-700 mb-3" style={{ fontFamily: 'Outfit' }}>Order Completion Documents</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {lead.completion_data.invoice_url && (
              <a href={`${BACKEND_URL}${lead.completion_data.invoice_url}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg border border-blue-200 text-sm text-blue-700 hover:bg-blue-100 transition" data-testid="completion-invoice">
                <FileText size={18} /> Invoice
              </a>
            )}
            {lead.completion_data.wcc_url && (
              <a href={`${BACKEND_URL}${lead.completion_data.wcc_url}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg border border-blue-200 text-sm text-blue-700 hover:bg-blue-100 transition" data-testid="completion-wcc">
                <FileText size={18} /> Work Completion Certificate
              </a>
            )}
            {lead.completion_data.csc_url && (
              <a href={`${BACKEND_URL}${lead.completion_data.csc_url}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg border border-blue-200 text-sm text-blue-700 hover:bg-blue-100 transition" data-testid="completion-csc">
                <FileText size={18} /> Customer Satisfaction Certificate
              </a>
            )}
          </div>
          <p className="text-xs text-[#6D8E87] mt-2">Completed by {lead.completion_data.completed_by_name} on {new Date(lead.completion_data.created_at).toLocaleDateString()}</p>
        </div>
      )}

      {/* LORD Record */}
      {lead.lord_record && (
        <div className="medical-card p-5 mb-6 border-2 border-red-200" data-testid="lord-section">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold text-red-700" style={{ fontFamily: 'Outfit' }}>Lost Order Review</h3>
            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${lead.lord_record.status === 'reviewed' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
              {lead.lord_record.status === 'reviewed' ? 'REVIEWED' : 'PENDING MANAGER REVIEW'}
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <div><span className="text-[#6D8E87]">Order went to:</span> <span className="font-medium">{lead.lord_record.q1_order_went_to}</span></div>
            <div><span className="text-[#6D8E87]">Pricing:</span> <span className="font-medium">{lead.lord_record.q3_pricing_competitiveness}</span></div>
            <div><span className="text-[#6D8E87]">Relationship:</span> <span className="font-medium">{lead.lord_record.q9_relationship_strength}</span></div>
            <div><span className="text-[#6D8E87]">Key reason:</span> <span className="font-medium">{lead.lord_record.key_reason_for_loss}</span></div>
          </div>
          {lead.lord_record.manager_comments && (
            <div className="mt-3 p-3 bg-green-50 rounded-lg border border-green-200">
              <p className="text-xs text-green-600 font-semibold mb-1">Manager Comment ({lead.lord_record.reviewed_by_name}):</p>
              <p className="text-sm text-green-800">{lead.lord_record.manager_comments}</p>
            </div>
          )}
        </div>
      )}

      {/* iter132 — Pending payment terms approval banner */}
      {!isTerminal && lead.pending_payment_terms_approval_id && (
        <div className="mt-6 p-4 rounded-xl border border-amber-200 bg-amber-50 text-amber-900 flex items-center justify-between gap-3" data-testid="pending-payment-terms-banner">
          <div className="text-sm">
            <span className="font-semibold">Payment terms sent for VP approval.</span> The lead will be marked Won automatically once approved. You can't re-submit until VP decides (or ask them to reject to try again).
          </div>
        </div>
      )}

      {/* Action Buttons */}
      {!isTerminal && (
        <div className="flex gap-4 mt-6" data-testid="action-buttons">
          <ScGatedButton
            action="edit"
            record={lead}
            onClick={() => setShowWonModal(true)}
            disabled={!!lead.pending_payment_terms_approval_id}
            className="flex-1 py-3 bg-emerald-600 text-white rounded-xl font-semibold flex items-center justify-center gap-2 hover:bg-emerald-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="order-won-btn"
            title={lead.pending_payment_terms_approval_id ? 'Waiting on VP approval for previously submitted terms' : ''}>
            <Trophy size={20} weight="bold" /> Order Won
          </ScGatedButton>
          <ScGatedButton action="edit" record={lead} onClick={() => navigate(`/leads/${id}/lord`)} className="flex-1 py-3 bg-red-500 text-white rounded-xl font-semibold flex items-center justify-center gap-2 hover:bg-red-600 transition" data-testid="order-lost-btn">
            <XCircle size={20} weight="bold" /> Order Lost
          </ScGatedButton>
          <ScGatedButton action="edit" record={lead} onClick={() => setShowExtendModal(true)} className="flex-1 py-3 bg-amber-500 text-white rounded-xl font-semibold flex items-center justify-center gap-2 hover:bg-amber-600 transition" data-testid="lead-extended-btn">
            <Clock size={20} weight="bold" /> Lead Extended
          </ScGatedButton>
        </div>
      )}

      {/* Order Complete Button (won only, not completed) */}
      {isWon && !isCompleted && (
        <div className="mt-6" data-testid="complete-action">
          {!isFullyPaid && (
            <div
              className="mb-3 p-3 rounded-xl border border-amber-300 bg-amber-50 text-amber-900 text-sm"
              data-testid="complete-payment-shortfall-banner"
            >
              <div className="font-semibold mb-1">Payment not fully collected</div>
              <div className="text-xs">
                Booked <span className="font-mono">₹{bookedValue.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                {' · '}Received <span className="font-mono">₹{paidValue.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                {' · '}Shortfall <span className="font-mono font-bold">₹{paymentShortfall.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="text-xs mt-1">Record the remaining payments below before marking the order complete.</div>
            </div>
          )}
          <ScGatedButton
            action="edit"
            record={lead}
            onClick={() => setShowCompleteModal(true)}
            disabled={!isFullyPaid}
            className={`w-full py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition ${
              isFullyPaid
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-blue-200 text-blue-500 cursor-not-allowed'
            }`}
            title={isFullyPaid ? 'Upload documents and mark this order complete' : 'Blocked until payments match the booked value'}
            data-testid="order-complete-btn"
          >
            <Package size={20} weight="bold" /> Order Complete
          </ScGatedButton>
        </div>
      )}

      {/* Order Won Modal */}
      <Dialog open={showWonModal} onOpenChange={setShowWonModal}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-xl font-bold text-[#052A23]" style={{ fontFamily: 'Outfit' }}>Order Won</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4" data-testid="won-modal">
            <div>
              <label className="block text-sm font-medium text-[#3B5A54] mb-1">Select Closing Quotation *</label>
              <select value={wonQuotationId} onChange={(e) => setWonQuotationId(e.target.value)} className="w-full px-4 py-2 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] outline-none bg-white" required data-testid="won-quotation-select">
                <option value="">-- Select Quotation --</option>
                {lead.quotations?.map(q => (
                  <option key={q.id} value={q.id}>₹{q.total.toLocaleString('en-IN')} ({q.status}) - {q.id.slice(0,8)}</option>
                ))}
              </select>
            </div>

            {/* Feb 2026 — Live PI status badge for the selected winning quote */}
            {wonQuotationId && wonPiStatus && (() => {
              const s = wonPiStatus.status || '';
              const isApproved = wonPiStatus.has_pi && s === 'approved';
              const tone = !wonPiStatus.has_pi ? 'rose'
                : isApproved ? 'emerald'
                : s === 'rejected' ? 'rose'
                : 'amber';
              if (s === 'rejected') {
                return (
                  <PiRejectedNotice
                    pi={wonPiStatus}
                    testid="won-pi-rejected"
                    onResubmitted={(u) => setWonPiStatus({ has_pi: true, ...u })}
                  />
                );
              }
              const bg = { rose: 'bg-rose-50 border-rose-200 text-rose-800',
                emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
                amber: 'bg-amber-50 border-amber-200 text-amber-800' }[tone];
              return (
                <div className={`flex items-center justify-between gap-3 px-3 py-2 rounded-lg border text-xs ${bg}`}
                  data-testid="won-pi-status-badge">
                  <div>
                    {!wonPiStatus.has_pi && <><span className="font-semibold">PI missing.</span> A Proforma Invoice must be created and approved before marking Won.</>}
                    {wonPiStatus.has_pi && isApproved && <><span className="font-semibold">PI {wonPiStatus.pi_number}</span> · Approved ✓ — ready to mark Won.</>}
                    {wonPiStatus.has_pi && !isApproved && s !== 'rejected' && (
                      <>
                        <span className="font-semibold">PI {wonPiStatus.pi_number}</span> · {(s || 'draft').replace(/_/g, ' ')} — must be Approved before mark-Won.
                      </>
                    )}

                  </div>
                  {!wonPiStatus.has_pi && (
                    <button type="button" onClick={generatePiInline} disabled={wonPiGenerating}
                      className="px-3 py-1 rounded-full text-[11px] font-semibold bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-60 whitespace-nowrap"
                      data-testid="won-pi-generate-btn">
                      {wonPiGenerating ? 'Generating…' : 'Generate now'}
                    </button>
                  )}
                  {wonPiStatus.has_pi && !isApproved && s !== 'rejected' && (
                    <button type="button" onClick={() => navigate('/proforma-invoices')}
                      className="px-3 py-1 rounded-full text-[11px] font-semibold border border-amber-600 text-amber-800 hover:bg-amber-100 whitespace-nowrap"
                      data-testid="won-pi-open-btn">
                      Open PI list
                    </button>
                  )}
                </div>
              );
            })()}

            <div>
              <label className="block text-sm font-medium text-[#3B5A54] mb-1">PO Number *</label>
              <input type="text" value={poNumber} onChange={(e) => setPoNumber(e.target.value)} className="w-full px-4 py-2 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] outline-none" placeholder="Enter Purchase Order number" required data-testid="po-number-input" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#3B5A54] mb-1">Upload PO Copy</label>
              <input type="file" onChange={(e) => setPoFile(e.target.files[0])} className="w-full px-4 py-2 border border-[#D5E8E4] rounded-lg" accept=".pdf,.jpg,.jpeg,.png" data-testid="po-file-input" />
            </div>
            {canEditFreight && (() => {
              const wq = (lead.quotations || []).find((q) => q.id === wonQuotationId);
              return (
                <div>
                  <label className="block text-sm font-medium text-[#3B5A54] mb-1">Actual freight cost (₹) <span className="text-xs text-[#6D8E87] font-normal">(optional)</span></label>
                  <input type="number" min="0" step="0.01" value={actualFreight} onChange={(e) => setActualFreight(e.target.value)}
                    className="w-full px-4 py-2 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] outline-none"
                    placeholder={`Leave blank to use transport charged${wq ? ` (₹${Number(wq.total_transport || 0).toLocaleString('en-IN')})` : ''}`}
                    data-testid="won-freight-cost-input" />
                </div>
              );
            })()}
            <div className="border-t border-[#D5E8E4] pt-4 mt-2">
              <h4 className="text-sm font-semibold text-[#052A23] mb-3">Payment Proof <span className="text-xs text-[#6D8E87] font-normal">(required)</span></h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-[#3B5A54] mb-1">Payment Date *</label>
                  <input type="date" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)}
                    className="w-full px-3 py-2 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] outline-none"
                    required data-testid="payment-date-input" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#3B5A54] mb-1">UTR / Transaction Ref *</label>
                  <input type="text" value={paymentUtr} onChange={(e) => setPaymentUtr(e.target.value)}
                    className="w-full px-3 py-2 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] outline-none"
                    placeholder="e.g., AXIS123456789" required data-testid="payment-utr-input" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#3B5A54] mb-1">Amount Received (₹)</label>
                  <input type="number" min="0" step="0.01" value={paymentAmount} onChange={(e) => setPaymentAmount(e.target.value)}
                    className="w-full px-3 py-2 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] outline-none"
                    placeholder="0.00" data-testid="payment-amount-input" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#3B5A54] mb-1">Payment Screenshot *</label>
                  <input type="file" onChange={(e) => setPaymentScreenshot(e.target.files[0])}
                    className="w-full px-3 py-2 border border-[#D5E8E4] rounded-lg text-xs"
                    accept=".pdf,.jpg,.jpeg,.png" required data-testid="payment-screenshot-input" />
                </div>
              </div>
              <div className="mt-3">
                <label className="block text-xs font-medium text-[#3B5A54] mb-1">Payment Notes (optional)</label>
                <textarea value={paymentNotes} onChange={(e) => setPaymentNotes(e.target.value)} rows={2}
                  className="w-full px-3 py-2 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] outline-none"
                  placeholder="Any additional context you want captured in the audit trail" data-testid="payment-notes-input" />
              </div>
            </div>

            {/* iter132 — Structured payment terms */}
            <div className="border-t border-[#D5E8E4] pt-4">
              <h4 className="text-sm font-semibold text-[#052A23] mb-1 flex items-center gap-2">
                Payment Terms <span className="text-rose-600" aria-hidden="true">*</span>
                <span className="text-xs text-[#6D8E87] font-normal">(structured — replaces old free-text notes)</span>
                <span className="ml-auto text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-rose-50 text-rose-700" data-testid="payment-terms-required-pill">Required</span>
              </h4>
              <p className="text-xs text-[#6D8E87] mb-3">Standard = Advance with PO + Before Dispatch summing to 100%. Anything else needs VP approval before the lead is marked Won.</p>
              {customerTemplates.length > 0 && (
                <div className="mb-3 p-2 rounded-lg border border-emerald-200 bg-emerald-50/60" data-testid="customer-templates-hint">
                  <div className="text-[11px] font-semibold text-emerald-800 uppercase tracking-wide mb-1">
                    Saved templates for this customer ({customerTemplates.length})
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {customerTemplates.slice(0, 5).map(tpl => (
                      <button
                        key={tpl.id}
                        type="button"
                        onClick={() => {
                          const t = tpl.payment_terms || {};
                          setPaymentTerms({
                            advance_with_po: { enabled: !!t.advance_with_po?.enabled, percent: Number(t.advance_with_po?.percent || 0) },
                            before_dispatch: { enabled: !!t.before_dispatch?.enabled, percent: Number(t.before_dispatch?.percent || 0) },
                            on_installation: { enabled: !!t.on_installation?.enabled, percent: Number(t.on_installation?.percent || 0) },
                            credit: { enabled: !!t.credit?.enabled, percent: Number(t.credit?.percent || 0), days: Number(t.credit?.days || 30) },
                          });
                        }}
                        className="px-2 py-1 rounded-full text-[11px] font-medium border border-emerald-300 bg-white text-emerald-800 hover:bg-emerald-50"
                        data-testid={`customer-template-chip-${tpl.id}`}
                        title="Apply this template (approval will be auto-bypassed)"
                      >
                        {tpl.payment_terms_summary}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <PaymentTermsPicker value={paymentTerms} onChange={setPaymentTerms} />
              {(() => {
                const tot = totalPercent(paymentTerms);
                const std = isStandardTerms(paymentTerms);
                const sumOk = Math.abs(tot - 100) < 0.01;
                // iter132.2 — template match preview: if a VP-approved
                // template with this exact canonical key exists for this
                // customer, tell the SE the Won will go straight through.
                const key = canonicalTermsKey(paymentTerms);
                const tplMatch = key ? customerTemplates.find(t => t.canonical_key === key) : null;
                let tone, msg;
                if (!sumOk) {
                  tone = 'rose';
                  msg = `Terms must total 100%. Currently ${tot.toFixed(1)}%.`;
                } else if (std) {
                  tone = 'emerald';
                  msg = 'Standard terms — will mark Won immediately.';
                } else if (tplMatch) {
                  tone = 'emerald';
                  msg = `Customer terms template match (${tplMatch.payment_terms_summary}) — approval will be auto-bypassed.`;
                } else {
                  tone = 'amber';
                  msg = 'Non-standard — will route to VP for approval before the lead is marked Won.';
                }
                const bg = { rose: 'bg-rose-50 border-rose-200 text-rose-800',
                             emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
                             amber:   'bg-amber-50 border-amber-200 text-amber-800' }[tone];
                return (
                  <div className={`mt-2 px-3 py-2 rounded-lg border text-xs ${bg}`} data-testid="payment-terms-status">
                    <span className="font-semibold">{tot.toFixed(1)}% total.</span> {msg}
                  </div>
                );
              })()}
            </div>
            <div className="flex gap-3 pt-2">
              {(() => {
                // iter132.5 — Compute a single boolean gate here so the
                // status banner text and the Confirm button share one
                // source of truth on payment-terms validity.
                const _tot = totalPercent(paymentTerms);
                const _anyEnabled = ['advance_with_po', 'before_dispatch', 'on_installation', 'credit'].some(k => paymentTerms[k]?.enabled);
                const _sumOk = Math.abs(_tot - 100) < 0.01;
                const _creditOk = !paymentTerms.credit?.enabled || Number(paymentTerms.credit?.days) > 0;
                const paymentTermsValid = _anyEnabled && _sumOk && _creditOk;
                const piNotApproved = wonQuotationId && wonPiStatus && !(wonPiStatus.has_pi && wonPiStatus.status === 'approved');
                let reason = '';
                if (piNotApproved) reason = 'PI must be Approved before marking the order Won';
                else if (!_anyEnabled) reason = 'Select at least one payment term';
                else if (!_sumOk) reason = `Payment terms must total 100% (currently ${_tot.toFixed(1)}%)`;
                else if (!_creditOk) reason = 'Credit term requires a positive number of days';
                return (
                  <>
                    <button onClick={() => setShowWonModal(false)} className="flex-1 px-6 py-3 border border-[#D5E8E4] rounded-lg hover:bg-gray-50 transition">Cancel</button>
                    <button onClick={handleOrderWon}
                      disabled={wonSubmitting || piNotApproved || !paymentTermsValid}
                      className="flex-1 py-3 bg-emerald-600 text-white rounded-lg font-semibold hover:bg-emerald-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="confirm-won-btn"
                      title={reason}>
                      {wonSubmitting ? 'Submitting...' : 'Confirm Order Won'}
                    </button>
                  </>
                );
              })()}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Extend Modal */}
      <Dialog open={showExtendModal} onOpenChange={setShowExtendModal}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="text-xl font-bold text-[#052A23]" style={{ fontFamily: 'Outfit' }}>Extend Lead</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4" data-testid="extend-modal">
            <p className="text-sm text-[#6D8E87]">Choose extension period for this lead:</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[15, 30, 45, 60].map(days => (
                <button key={days} onClick={() => setExtensionDays(days)}
                  className={`p-4 rounded-xl border-2 text-center font-semibold transition ${extensionDays === days ? 'border-[#00a69c] bg-[#E2F3F0] text-[#00a69c]' : 'border-[#D5E8E4] text-[#3B5A54] hover:border-[#00a69c]'}`}
                  data-testid={`extend-${days}-btn`}>
                  {days} Days
                </button>
              ))}
            </div>
            <div className="flex gap-3 pt-2">
              <button onClick={() => setShowExtendModal(false)} className="flex-1 px-6 py-3 border border-[#D5E8E4] rounded-lg hover:bg-gray-50 transition">Cancel</button>
              <button onClick={handleExtend} className="flex-1 py-3 bg-amber-500 text-white rounded-lg font-semibold hover:bg-amber-600 transition" data-testid="confirm-extend-btn">
                Extend by {extensionDays} Days
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Order Complete Modal */}
      <Dialog open={showCompleteModal} onOpenChange={setShowCompleteModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="text-xl font-bold text-blue-700" style={{ fontFamily: 'Outfit' }}>Order Complete</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4" data-testid="complete-modal">
            <p className="text-sm text-[#6D8E87]">Upload the following documents to mark this order as complete:</p>
            <div>
              <label className="block text-sm font-medium text-[#3B5A54] mb-1">Invoice (Image/PDF) *</label>
              <input type="file" onChange={(e) => setInvoiceFile(e.target.files[0])} className="w-full px-4 py-2 border border-[#D5E8E4] rounded-lg" accept=".pdf,.jpg,.jpeg,.png" data-testid="invoice-file-input" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#3B5A54] mb-1">Work Completion Certificate (Image/PDF) *</label>
              <input type="file" onChange={(e) => setWccFile(e.target.files[0])} className="w-full px-4 py-2 border border-[#D5E8E4] rounded-lg" accept=".pdf,.jpg,.jpeg,.png" data-testid="wcc-file-input" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#3B5A54] mb-1">Customer Satisfaction Certificate (Image/PDF) *</label>
              <input type="file" onChange={(e) => setCscFile(e.target.files[0])} className="w-full px-4 py-2 border border-[#D5E8E4] rounded-lg" accept=".pdf,.jpg,.jpeg,.png" data-testid="csc-file-input" />
            </div>
            <div className="flex gap-3 pt-2">
              <button onClick={() => setShowCompleteModal(false)} className="flex-1 px-6 py-3 border border-[#D5E8E4] rounded-lg hover:bg-gray-50 transition">Cancel</button>
              <button onClick={handleOrderComplete} disabled={completeSubmitting} className="flex-1 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition disabled:opacity-50" data-testid="confirm-complete-btn">
                {completeSubmitting ? 'Submitting...' : 'Complete Order'}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reassign Modal */}
      <Dialog open={showReassignModal} onOpenChange={(v) => { setShowReassignModal(v); if (!v) setCrossRegionInfo(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#052A23]" style={{ fontFamily: 'Outfit' }}>
              Reassign Lead
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2" data-testid="reassign-modal">
            <div className="p-3 bg-[#F4F9F7] rounded-lg text-sm">
              <div className="text-[#6D8E87] text-xs mb-1">Lead</div>
              <div className="font-medium text-[#052A23]">{lead.name} — {lead.company}</div>
              <div className="text-xs text-[#6D8E87] mt-0.5">
                Current owner: <span className="font-medium text-[#3B5A54]">{lead.created_by_name || '—'}</span>
                {lead.region && <span> · Lead region: <span className="font-medium text-[#3B5A54]">{lead.region}</span></span>}
              </div>
            </div>

            {!crossRegionInfo && (
              <>
                <div>
                  <label className="block text-sm font-medium text-[#3B5A54] mb-1">New Assignee *</label>
                  <select value={reassignTarget} onChange={(e) => setReassignTarget(e.target.value)}
                    className="w-full px-4 py-2 border border-[#D5E8E4] rounded-lg bg-white" required data-testid="reassign-target-select">
                    <option value="">-- Select team member --</option>
                    {salesExecs.filter(u => u.id !== lead.created_by).map(u => (
                      <option key={u.id} value={u.id}>
                        {u.name} — {u.role}{u.region ? ` (${u.region})` : ' (No region)'}
                      </option>
                    ))}
                  </select>
                  {salesExecs.length === 0 && <p className="text-xs text-amber-600 mt-1">No team members found</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#3B5A54] mb-1">Reason (optional)</label>
                  <textarea rows={2} value={reassignReason} onChange={(e) => setReassignReason(e.target.value)}
                    placeholder="e.g. John on leave, balancing workload, or leaving the company"
                    className="w-full px-4 py-2 border border-[#D5E8E4] rounded-lg outline-none focus:ring-2 focus:ring-[#00bfb3]"
                    data-testid="reassign-reason" />
                </div>
                <div className="flex gap-3 pt-2">
                  <button onClick={() => setShowReassignModal(false)}
                    className="flex-1 px-6 py-3 border border-[#D5E8E4] rounded-lg hover:bg-gray-50">Cancel</button>
                  <button onClick={() => submitReassign(false)} disabled={reassignSubmitting || !reassignTarget}
                    className="flex-1 py-3 bg-[#00a69c] text-white rounded-lg font-semibold hover:bg-[#00bfb3] transition disabled:opacity-50"
                    data-testid="reassign-submit-btn">
                    {reassignSubmitting ? 'Reassigning...' : 'Reassign Lead'}
                  </button>
                </div>
              </>
            )}

            {crossRegionInfo && (
              <div className="p-4 bg-amber-50 border-2 border-amber-300 rounded-xl" data-testid="cross-region-warning">
                <div className="flex items-start gap-3 mb-3">
                  <WarningOctagon size={28} weight="fill" className="text-amber-500 shrink-0" />
                  <div>
                    <h4 className="text-base font-bold text-amber-900" style={{ fontFamily: 'Outfit' }}>Region Mismatch</h4>
                    <p className="text-sm text-amber-800 mt-1">
                      This lead is in <span className="font-semibold">{crossRegionInfo.lead_region}</span>, but you're assigning to{' '}
                      <span className="font-semibold">{crossRegionInfo.target_name}</span> in the{' '}
                      <span className="font-semibold">{crossRegionInfo.target_region}</span> region.
                    </p>
                    <p className="text-xs text-amber-700 mt-2">
                      If you proceed, the Regional Sales Managers of both regions will be notified.
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <button onClick={() => setCrossRegionInfo(null)}
                    className="flex-1 px-4 py-2.5 border border-amber-400 text-amber-800 rounded-lg hover:bg-amber-100 text-sm font-medium"
                    data-testid="cross-region-cancel-btn">
                    Cancel
                  </button>
                  <button onClick={() => submitReassign(true)} disabled={reassignSubmitting}
                    className="flex-1 px-4 py-2.5 bg-amber-500 text-white rounded-lg hover:bg-amber-600 text-sm font-semibold disabled:opacity-50"
                    data-testid="cross-region-confirm-btn">
                    {reassignSubmitting ? 'Reassigning...' : 'Assign Anyway'}
                  </button>
                </div>
              </div>
            )}

            {/* History */}
            {reassignHistory.length > 0 && (
              <div className="pt-3 border-t border-[#F4F9F7]">
                <h4 className="text-xs uppercase font-semibold text-[#6D8E87] mb-2 tracking-wide">Reassignment History</h4>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {reassignHistory.map(h => (
                    <div key={h.id} className="text-xs p-2 bg-[#F4F9F7] rounded-lg" data-testid={`reassign-history-${h.id}`}>
                      <div className="text-[#052A23]">
                        <span className="font-medium">{h.from_user_name || '—'}</span> → <span className="font-medium">{h.to_user_name}</span>
                        {h.cross_region && <span className="ml-1 px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded text-[10px] font-semibold">CROSS-REGION</span>}
                      </div>
                      <div className="text-[#6D8E87] mt-0.5">
                        by {h.actor_name} · {new Date(h.created_at).toLocaleDateString()}
                        {h.reason && <span className="italic"> · "{h.reason}"</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LeadDetail;
